/**
 * Light, dark, or whatever the operating system says.
 *
 * `tokens.css` carries the whole dark palette on `:root[data-theme="dark"]`, so switching schemes
 * is one attribute on `<html>` — every colour written with `token()` follows it without a re-render.
 * Material UI's palette cannot read a custom property, so the resolved mode is also handed to
 * `buildTheme()`.
 *
 * The attribute is applied at module load, before React mounts, so a viewer who chose light does not
 * see a dark frame flash first. The preference is a display setting and lives in `localStorage`;
 * nothing sensitive goes there.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { ColorMode } from '@/theme/tokens';

export type ColorModePreference = ColorMode | 'system';

const STORAGE_KEY = 'opoc.color-scheme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readPreference(): ColorModePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing and locked-down profiles both throw here. The default is still correct.
  }
  return 'system';
}

function systemMode(): ColorMode {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
    ? 'dark'
    : 'light';
}

function resolve(preference: ColorModePreference): ColorMode {
  return preference === 'system' ? systemMode() : preference;
}

/**
 * Pin the resolved scheme onto `<html>`.
 *
 * Always writing the attribute — never leaving it off for "system" — is what makes the two blocks
 * in `tokens.css` agree with the palette Material UI was built with. `colorScheme` tells the browser
 * to match its own form controls and scrollbars.
 */
function apply(mode: ColorMode): void {
  const root = document.documentElement;
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
}

// Runs on import, before the first render.
if (typeof document !== 'undefined') apply(resolve(readPreference()));

interface ColorModeContextValue {
  /** What is actually on screen. */
  mode: ColorMode;
  /** What the viewer asked for, which may be "system". */
  preference: ColorModePreference;
  setPreference: (preference: ColorModePreference) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (!context) throw new Error('useColorMode must be used inside the app providers.');
  return context;
}

export function ColorModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [preference, setPreferenceState] = useState<ColorModePreference>(readPreference);
  const [mode, setMode] = useState<ColorMode>(() => resolve(readPreference()));

  const setPreference = useCallback((next: ColorModePreference) => {
    setPreferenceState(next);
    setMode(resolve(next));
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies to this session.
    }
  }, []);

  useEffect(() => {
    apply(mode);
  }, [mode]);

  // Following the system means following it as it changes, not only at load.
  useEffect(() => {
    if (preference !== 'system' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => setMode(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const value = useMemo(
    () => ({ mode, preference, setPreference }),
    [mode, preference, setPreference],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}
